import React, { useEffect, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-component-support';
import { FlightDeckClient } from '@atlassiansox/flight-deck-frontend-client';

import {
	type AppRecommendationsArtifact,
	type Options,
	type TargetType,
	type WithAppProvisionProps,
} from './types';

export const useAppProvisioning = () => {
	const crossFlow = useCrossFlow();

	const [appKeyMappings, setAppKeyMappings] = useState<AppRecommendationsArtifact>();

	useEffect(() => {
		const client = new FlightDeckClient('app-provisioning');

		client
			.fetchConfigFromFlightDeck({
				name: 'discovery-sdk',
				dimensions: {
					environmentType: 'prod',
				},
			})
			.then((mappings) => setAppKeyMappings(mappings as AppRecommendationsArtifact))
			.catch(() => {});
	}, []);

	return ({ productId, ...rest }: Options) => {
		// TODO: check if the app is 1P, 2P or 3P and create the provision context accordingly.
		// This can be done through Unified Listing API.
		// https://hello.atlassian.net/wiki/spaces/ECON/pages/2303960596/Project+Plan+Unified+Listing+API
		// At the moment, we only support provisioning 1P apps.

		if (!crossFlow.isEnabled || !appKeyMappings || !appKeyMappings.products[productId]) {
			return Promise.resolve({ success: false });
		}

		const { crossFlowReferenceId } = appKeyMappings?.products[productId];

		return crossFlow.api.open({
			targetProduct: crossFlowReferenceId as TargetType,
			...rest,
		});
	};
};

export function withAppProvisioning<P>(
	WrappedComponent: React.ComponentType<P & WithAppProvisionProps>,
): React.ComponentType<P> {
	const ComponentWithAppProvisioning = (props: P) => {
		const appProvisioning = useAppProvisioning();

		return <WrappedComponent {...props} appProvisioning={appProvisioning} />;
	};

	ComponentWithAppProvisioning.displayName = `withAppProvisioning(${
		WrappedComponent.displayName || WrappedComponent.name
	})`;

	return ComponentWithAppProvisioning;
}
